import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Button,
  Tag,
  Avatar,
  Spin,
  Input,
  Empty,
} from 'antd';
import {
  CustomerServiceOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  StopOutlined,
  AudioOutlined,
  DownOutlined,
  UpOutlined,
  UserOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { callCenterLiveApi } from '../../utils/api';
import { useSocket, useSocketEvent } from '../../hooks/useSocket';
import { useTheme } from '../../contexts/ThemeContext';
import PermissionGate from '../../components/PermissionGate';

const formatSeconds = (sec) => {
  if (!sec || isNaN(sec)) return '00:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const LiveCallCenter = () => {
  const { isDarkMode } = useTheme();
  const { socket, isConnected, emit } = useSocket();

  const [loading, setLoading] = useState(true);
  const [queues, setQueues] = useState([]);
  const [unassigned, setUnassigned] = useState(null);

  // Real-time presence and call states from karmadws
  // onlineExtensions: Set of extension strings
  // onlineUsers: Set of user_id numbers
  // busyExtensions: Map of extension -> call data
  const [onlineExtensions, setOnlineExtensions] = useState(new Set());
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [busyExtensions, setBusyExtensions] = useState(new Map());

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'busy', 'free', 'offline'
  const [collapsedQueues, setCollapsedQueues] = useState({});

  // 1-second interval to tick active calls duration
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 1. Fetch all operators using Laravel API and set them offline initially
  const loadInitialOperators = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callCenterLiveApi.getLiveStatus();
      const payload = res?.data || {};

      // All operators from Laravel API are initialized with status: 'offline'
      const initQueues = (payload.queues || []).map((q) => ({
        ...q,
        agents: (q.agents || []).map((agent) => ({
          ...agent,
          status: 'offline', // Set offline initially as requested
          live_call: null,
        })),
      }));

      let initUnassigned = null;
      if (payload.unassigned) {
        initUnassigned = {
          ...payload.unassigned,
          agents: (payload.unassigned.agents || []).map((agent) => ({
            ...agent,
            status: 'offline', // Set offline initially as requested
            live_call: null,
          })),
        };
      }

      setQueues(initQueues);
      setUnassigned(initUnassigned);
    } catch (err) {
      console.error('خطا در دریافت لیست اپراتورها از سرور:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialOperators();
  }, [loadInitialOperators]);

  // Apply live presence and call snapshot
  const applyLiveSnapshot = useCallback((snapshot) => {
    if (!snapshot) return;

    if (Array.isArray(snapshot.online_extensions)) {
      setOnlineExtensions(new Set(snapshot.online_extensions.map(String)));
    }
    if (Array.isArray(snapshot.online_users)) {
      setOnlineUsers(new Set(snapshot.online_users.map(Number)));
    }
    if (snapshot.busy_extensions && typeof snapshot.busy_extensions === 'object') {
      const busyMap = new Map();
      Object.entries(snapshot.busy_extensions).forEach(([ext, call]) => {
        if (call) {
          busyMap.set(String(ext), {
            ...call,
            started_at: call.started_at ? new Date(call.started_at) : new Date(),
          });
        }
      });
      setBusyExtensions(busyMap);
    }
  }, []);

  // 2. Connect to socket server and request live operators status
  useEffect(() => {
    if (socket && isConnected) {
      // Join live call center room
      emit('join-live-callcenter');

      // Request live presence & call snapshot
      emit('get-live-snapshot', (snapshot) => {
        applyLiveSnapshot(snapshot);
      });
    }
  }, [socket, isConnected, emit, applyLiveSnapshot]);

  // Listen to live snapshot broadcast
  useSocketEvent('live_snapshot', applyLiveSnapshot, isConnected);

  // 3. karmadws event: operator connects -> becomes free
  const handleOperatorOnline = useCallback((data) => {
    if (!data) return;
    const { user_id, extensions } = data;

    if (user_id) {
      setOnlineUsers((prev) => new Set(prev).add(Number(user_id)));
    }
    if (Array.isArray(extensions)) {
      setOnlineExtensions((prev) => {
        const next = new Set(prev);
        extensions.forEach((ext) => next.add(String(ext)));
        return next;
      });
    }
  }, []);
  useSocketEvent('operator_online', handleOperatorOnline, isConnected);

  // 4. karmadws event: operator disconnects -> becomes offline
  const handleOperatorOffline = useCallback((data) => {
    if (!data) return;
    const { user_id, extensions, userOffline } = data;

    if (userOffline && user_id) {
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        next.delete(Number(user_id));
        return next;
      });
    }
    if (Array.isArray(extensions)) {
      setOnlineExtensions((prev) => {
        const next = new Set(prev);
        extensions.forEach((ext) => next.delete(String(ext)));
        return next;
      });
    }
  }, []);
  useSocketEvent('operator_offline', handleOperatorOffline, isConnected);

  // 5. karmadws call event: ringing / talking -> busy
  const handleCallBusy = useCallback((callPayload) => {
    const rawExt = callPayload?.extension || callPayload?.data?.extension || callPayload?.call?.extension;
    const ext = rawExt ? String(rawExt).trim() : null;
    if (!ext) return;

    setBusyExtensions((prev) => {
      const next = new Map(prev);
      const call = callPayload?.data || callPayload;
      const current = prev.get(ext);
      // Repeated events of the same call keep the timer; ringing -> answered restarts it.
      const sameState = current && current.call_id === call?.call_id && current.event === call?.event;
      next.set(ext, {
        ...call,
        started_at: sameState ? current.started_at : new Date(),
      });
      return next;
    });
  }, []);

  // 6. karmadws call event: hangup / hangout -> free
  const handleCallFree = useCallback((callPayload) => {
    const rawExt = callPayload?.extension || callPayload?.data?.extension || callPayload?.call?.extension;
    const ext = rawExt ? String(rawExt).trim() : null;
    if (!ext) return;

    setBusyExtensions((prev) => {
      const next = new Map(prev);
      next.delete(ext);
      return next;
    });
  }, []);

  // Direct call status event from karmadws
  const handleOperatorCallStatus = useCallback(
    ({ extension, is_busy, call }) => {
      const ext = extension ? String(extension).trim() : null;
      if (!ext) return;

      if (is_busy) {
        handleCallBusy({ extension: ext, ...call });
      } else {
        handleCallFree({ extension: ext });
      }
    },
    [handleCallBusy, handleCallFree],
  );

  // karmadws is the single source of truth for busy extensions: it frees the
  // other extensions of a queue call once answered and expires stale calls.
  useSocketEvent('operator_call_status', handleOperatorCallStatus, isConnected);

  // Periodically re-sync with the server snapshot so a missed event never
  // leaves an operator busy with an ever-growing timer.
  useEffect(() => {
    if (!socket || !isConnected) return undefined;
    const timer = setInterval(() => {
      emit('get-live-snapshot', (snapshot) => applyLiveSnapshot(snapshot));
    }, 30000);
    return () => clearInterval(timer);
  }, [socket, isConnected, emit, applyLiveSnapshot]);

  // Helper to compute live status of an operator using karmadws state
  const getOperatorLiveStatus = useCallback(
    (agent) => {
      const ext = String(agent.extension || '').trim();
      const userId = Number(agent.user_id);

      const liveCall = busyExtensions.get(ext) || null;
      if (liveCall) {
        return {
          status: 'busy',
          live_call: liveCall,
        };
      }

      const isOnline = onlineExtensions.has(ext) || onlineUsers.has(userId);
      if (isOnline) {
        return {
          status: 'free',
          live_call: null,
        };
      }

      return {
        status: 'offline',
        live_call: null,
      };
    },
    [busyExtensions, onlineExtensions, onlineUsers],
  );

  // Grouped queues with live statuses computed from karmadws
  const liveQueues = useMemo(() => {
    const list = [...queues];
    if (unassigned && (unassigned.agents || []).length > 0) {
      list.push(unassigned);
    }

    return list.map((q) => {
      const liveAgents = (q.agents || []).map((agent) => {
        const live = getOperatorLiveStatus(agent);
        return {
          ...agent,
          status: live.status,
          live_call: live.live_call,
        };
      });

      return {
        ...q,
        agents: liveAgents,
      };
    });
  }, [queues, unassigned, getOperatorLiveStatus]);

  // Calculate ONLY Busy and Free KPI counts across all operators
  const { busyCount, freeCount } = useMemo(() => {
    let busy = 0;
    let free = 0;

    liveQueues.forEach((q) => {
      (q.agents || []).forEach((a) => {
        if (a.status === 'busy') busy += 1;
        else if (a.status === 'free') free += 1;
      });
    });

    return { busyCount: busy, freeCount: free };
  }, [liveQueues]);

  // Filter queues and operators by search query and status filter
  const filteredQueues = useMemo(() => {
    return liveQueues
      .map((q) => {
        const matchingAgents = (q.agents || []).filter((agent) => {
          // Status filter
          if (statusFilter !== 'all' && agent.status !== statusFilter) {
            return false;
          }

          // Search query
          if (searchQuery.trim()) {
            const query = searchQuery.trim().toLowerCase();
            const name = (agent.name || '').toLowerCase();
            const ext = (agent.extension || '').toLowerCase();
            const qTitle = (q.title || '').toLowerCase();
            return name.includes(query) || ext.includes(query) || qTitle.includes(query);
          }

          return true;
        });

        return {
          ...q,
          filteredAgents: matchingAgents,
        };
      })
      .filter((q) => q.filteredAgents.length > 0 || !searchQuery);
  }, [liveQueues, statusFilter, searchQuery]);

  // Toggle collapse for a queue
  const toggleQueueCollapse = (queueKey) => {
    setCollapsedQueues((prev) => ({
      ...prev,
      [queueKey]: !prev[queueKey],
    }));
  };

  return (
    <MainLayout
      title="مرکز تماس زنده"
      activeMenu="/call-center/live"
      breadcrumbs={[
        { title: 'مرکز تماس', path: '/call-center/calls' },
        { title: 'مرکز تماس زنده' },
      ]}
    >
      <PermissionGate module="callcenter" permission="view_callcenter" showWarning>
        <div className="w-full space-y-5 pb-10">
          {/* Header Card */}
          <div className={`bg-gradient-to-r from-slate-900 via-slate-800 to-teal-950 text-white rounded-2xl p-5 shadow-lg border ${
            isDarkMode ? 'border-[rgba(13,136,126,0.3)]' : 'border-slate-700/50'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-teal-500/20 border border-teal-400/30 flex items-center justify-center text-teal-300 text-2xl shadow-inner">
                  <CustomerServiceOutlined />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-bold text-white mb-0">مرکز تماس زنده (Live Call Center)</h1>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse mr-1.5 ml-0"></span>
                      ارتباط لحظه‌ای
                    </span>
                  </div>
                  <p className="text-slate-300 text-xs mt-1 mb-0">
                    وضعیت لحظه‌ای اپراتورها بر اساس رویدادهای زنگ خوردن (Ringing) و اتمام مکالمه (Hangup)
                  </p>
                </div>
              </div>

              {/* Socket connection indicator & refresh */}
              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border ${
                    isConnected
                      ? 'bg-emerald-950/60 text-emerald-300 border-emerald-500/30'
                      : 'bg-amber-950/60 text-amber-300 border-amber-500/30'
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      isConnected ? 'bg-emerald-400 animate-ping' : 'bg-amber-400'
                    }`}
                  />
                  <span>{isConnected ? 'ارتباط برقرار است' : 'در حال اتصال به سرور...'}</span>
                </div>

                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadInitialOperators}
                  className="bg-slate-800 text-white border-slate-700 hover:bg-slate-700 text-xs"
                >
                  بارگذاری مجدد
                </Button>
              </div>
            </div>
          </div>

          {/* ONLY Busy & Free KPI Cards as requested */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Busy Operators KPI */}
            <div
              onClick={() => setStatusFilter(statusFilter === 'busy' ? 'all' : 'busy')}
              className={`cursor-pointer transition-all duration-200 rounded-2xl p-5 border shadow-sm flex items-center justify-between ${
                statusFilter === 'busy'
                  ? isDarkMode
                    ? 'bg-rose-950/40 border-rose-500 ring-2 ring-rose-500/40 shadow-md'
                    : 'bg-rose-50 border-rose-500 ring-2 ring-rose-300 shadow-md'
                  : isDarkMode
                  ? 'bg-[#070B14] border-[rgba(13,136,126,0.22)] hover:border-rose-400/50'
                  : 'bg-white border-gray-200 hover:border-rose-300 hover:shadow'
              }`}
            >
              <div className="space-y-1">
                <div className={`flex items-center gap-2 font-bold text-sm ${isDarkMode ? 'text-rose-400' : 'text-rose-700'}`}>
                  <AudioOutlined className="animate-pulse text-lg" />
                  <span>اپراتورهای مشغول (Busy)</span>
                </div>
                <p className={`text-xs mb-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  اپراتورهایی که تلفن آن‌ها در حال زنگ خوردن است یا در حال مکالمه هستند
                </p>
              </div>

              <div className="text-left">
                <div className="flex items-baseline gap-1.5 justify-end">
                  <span className={`text-3xl font-extrabold font-mono ${isDarkMode ? 'text-rose-400' : 'text-rose-600'}`}>{busyCount}</span>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-rose-400/80' : 'text-rose-500'}`}>اپراتور</span>
                </div>
                <Tag color="error" className="m-0 rounded-full text-[11px] font-semibold border-0">
                  مشغول مکالمه / زنگ
                </Tag>
              </div>
            </div>

            {/* Free Operators KPI */}
            <div
              onClick={() => setStatusFilter(statusFilter === 'free' ? 'all' : 'free')}
              className={`cursor-pointer transition-all duration-200 rounded-2xl p-5 border shadow-sm flex items-center justify-between ${
                statusFilter === 'free'
                  ? isDarkMode
                    ? 'bg-emerald-950/40 border-emerald-500 ring-2 ring-emerald-500/40 shadow-md'
                    : 'bg-emerald-50 border-emerald-500 ring-2 ring-emerald-300 shadow-md'
                  : isDarkMode
                  ? 'bg-[#070B14] border-[rgba(13,136,126,0.22)] hover:border-emerald-400/50'
                  : 'bg-white border-gray-200 hover:border-emerald-300 hover:shadow'
              }`}
            >
              <div className="space-y-1">
                <div className={`flex items-center gap-2 font-bold text-sm ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  <CheckCircleOutlined className={`text-lg ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <span>اپراتورهای آزاد (Free)</span>
                </div>
                <p className={`text-xs mb-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  اپراتورهای متصل به سیستم که خط آن‌ها آزاد و آماده دریافت تماس است
                </p>
              </div>

              <div className="text-left">
                <div className="flex items-baseline gap-1.5 justify-end">
                  <span className={`text-3xl font-extrabold font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{freeCount}</span>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-emerald-400/80' : 'text-emerald-500'}`}>اپراتور</span>
                </div>
                <Tag color="success" className="m-0 rounded-full text-[11px] font-semibold border-0">
                  آماده دریافت تماس
                </Tag>
              </div>
            </div>
          </div>

          {/* Search & Filter Strip */}
          <div
            className={`rounded-2xl border p-4 transition-all duration-200 ${
              isDarkMode
                ? 'bg-[#070B14] border-[rgba(13,136,126,0.22)]'
                : 'bg-white border-gray-200 shadow-sm'
            }`}
          >
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="flex-1 max-w-md">
                <Input
                  placeholder="جستجوی نام اپراتور، داخلی یا صف..."
                  prefix={<SearchOutlined className={isDarkMode ? 'text-gray-500' : 'text-gray-400'} />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                  className="rounded-lg custom-font text-xs"
                />
              </div>

              {/* Filter Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto py-1">
                <button
                  type="button"
                  onClick={() => setStatusFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer border ${
                    statusFilter === 'all'
                      ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                      : isDarkMode
                      ? 'bg-[#0b1220] text-gray-300 hover:bg-[#101b2f] border-[rgba(13,136,126,0.22)]'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-200'
                  }`}
                >
                  همه اپراتورها
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('busy')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer border ${
                    statusFilter === 'busy'
                      ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                      : isDarkMode
                      ? 'bg-rose-950/30 text-rose-300 hover:bg-rose-900/40 border-rose-800/40'
                      : 'bg-rose-50 text-rose-700 hover:bg-rose-100 border-rose-200'
                  }`}
                >
                  مشغول ({busyCount})
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('free')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer border ${
                    statusFilter === 'free'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                      : isDarkMode
                      ? 'bg-emerald-950/30 text-emerald-300 hover:bg-emerald-900/40 border-emerald-800/40'
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200'
                  }`}
                >
                  آزاد ({freeCount})
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('offline')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer border ${
                    statusFilter === 'offline'
                      ? isDarkMode
                        ? 'bg-gray-700 text-white border-gray-600 shadow-sm'
                        : 'bg-gray-600 text-white border-gray-600 shadow-sm'
                      : isDarkMode
                      ? 'bg-[#0b1220] text-gray-400 hover:bg-[#101b2f] border-[rgba(13,136,126,0.22)]'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-200'
                  }`}
                >
                  آفلاین
                </button>
              </div>
            </div>
          </div>

          {/* Queues and Operator Cards */}
          {loading ? (
            <div
              className={`flex flex-col items-center justify-center py-20 rounded-2xl border ${
                isDarkMode
                  ? 'bg-[#070B14] border-[rgba(13,136,126,0.22)]'
                  : 'bg-white border-gray-200 shadow-sm'
              }`}
            >
              <Spin size="large" />
              <span className={`mt-4 font-medium text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                در حال دریافت لیست صف‌ها و اپراتورها...
              </span>
            </div>
          ) : filteredQueues.length === 0 ? (
            <div
              className={`rounded-2xl p-12 text-center border ${
                isDarkMode
                  ? 'bg-[#070B14] border-[rgba(13,136,126,0.22)] text-gray-400'
                  : 'bg-white border-gray-200 shadow-sm'
              }`}
            >
              <Empty
                description={
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    هیچ اپراتوری با فیلتر انتخابی یافت نشد.
                  </span>
                }
              />
            </div>
          ) : (
            <div className="space-y-5">
              {filteredQueues.map((queue) => {
                const qKey = queue.id === null ? 'unassigned' : queue.id;
                const isCollapsed = Boolean(collapsedQueues[qKey]);
                const agents = queue.filteredAgents || [];

                return (
                  <div
                    key={qKey}
                    className={`rounded-2xl border shadow-sm overflow-hidden transition-all duration-200 ${
                      isDarkMode
                        ? 'bg-[#070B14] border-[rgba(13,136,126,0.22)]'
                        : 'bg-white border-gray-200/80'
                    }`}
                  >
                    {/* Queue Header */}
                    <div
                      onClick={() => toggleQueueCollapse(qKey)}
                      className={`p-4 sm:px-6 border-b flex items-center justify-between gap-3 cursor-pointer select-none transition-colors ${
                        isDarkMode
                          ? 'bg-[#0b1220] border-[rgba(13,136,126,0.22)] hover:bg-[#0e1728]'
                          : 'bg-gradient-to-r from-slate-50 via-gray-50 to-white border-gray-100 hover:bg-slate-100/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center text-base ${
                            queue.id === null
                              ? isDarkMode
                                ? 'bg-amber-950/40 text-amber-400 border border-amber-800/40'
                                : 'bg-amber-100 text-amber-700 border border-amber-200'
                              : isDarkMode
                              ? 'bg-teal-950/40 text-teal-300 border border-teal-800/40'
                              : 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                          }`}
                        >
                          {queue.id === null ? <UserOutlined /> : <CustomerServiceOutlined />}
                        </div>

                        <div className="flex items-center gap-2">
                          <h2 className={`text-sm sm:text-base font-bold mb-0 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {queue.title || queue.name}
                          </h2>
                          {queue.name && queue.name !== queue.title && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded font-mono ${
                                isDarkMode
                                  ? 'bg-gray-800 text-gray-300 border border-gray-700'
                                  : 'bg-gray-200/80 text-gray-600'
                              }`}
                              dir="ltr"
                            >
                              {queue.name}
                            </span>
                          )}
                          <Tag
                            className={`rounded-full text-xs custom-font border-0 mr-2 ${
                              isDarkMode
                                ? 'bg-teal-950/60 text-teal-300 border border-teal-800/40'
                                : 'bg-indigo-50 text-indigo-700'
                            }`}
                          >
                            {agents.length} اپراتور
                          </Tag>
                        </div>
                      </div>

                      <div className={isDarkMode ? 'text-gray-400' : 'text-gray-400'}>
                        {isCollapsed ? <DownOutlined /> : <UpOutlined />}
                      </div>
                    </div>

                    {/* Queue Body: Operators Grid */}
                    {!isCollapsed && (
                      <div className={`p-4 sm:p-5 ${isDarkMode ? 'bg-[#05080f]' : 'bg-slate-50/40'}`}>
                        {agents.length === 0 ? (
                          <div className={`text-center py-6 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            اپراتوری با این فیلتر در این صف وجود ندارد.
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3.5">
                            {agents.map((agent) => (
                              <OperatorLiveCard key={agent.id} agent={agent} isDarkMode={isDarkMode} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PermissionGate>
    </MainLayout>
  );
};

/** Operator Card: Busy, Free or Offline */
const OperatorLiveCard = ({ agent, isDarkMode }) => {
  const isBusy = agent.status === 'busy';
  const isFree = agent.status === 'free';
  const liveCall = agent.live_call;

  // Compute duration if live call is active
  // Recomputed on every render (the page re-renders each second) so the timer ticks.
  const elapsedSec = liveCall?.started_at
    ? Math.max(0, Math.floor((Date.now() - new Date(liveCall.started_at).getTime()) / 1000))
    : 0;

  let cardBorderBg = '';
  if (isBusy) {
    cardBorderBg = isDarkMode
      ? 'bg-[#120a10] border-rose-500/60 ring-1 ring-rose-500/30'
      : 'border-rose-400 ring-2 ring-rose-200/60 bg-rose-50/20';
  } else if (isFree) {
    cardBorderBg = isDarkMode
      ? 'bg-[#081313] border-emerald-500/50 ring-1 ring-emerald-500/20'
      : 'border-emerald-300 ring-1 ring-emerald-200/60 bg-white';
  } else {
    cardBorderBg = isDarkMode
      ? 'bg-[#070B14] border-[rgba(13,136,126,0.18)] opacity-70 hover:opacity-100'
      : 'border-gray-200 bg-white opacity-80 hover:opacity-100';
  }

  return (
    <div
      className={`rounded-xl border transition-all duration-200 p-3.5 flex flex-col justify-between shadow-sm hover:shadow ${cardBorderBg}`}
    >
      <div className="space-y-2.5">
        {/* Top: Avatar, Name & Extension */}
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <Avatar
              size={40}
              src={agent.avatar}
              icon={<UserOutlined />}
              className={
                isDarkMode
                  ? 'bg-teal-950 text-teal-300 border border-teal-800/40'
                  : 'bg-indigo-100 text-indigo-700 border border-gray-200'
              }
            />
            <span
              className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                isDarkMode ? 'border-[#070B14]' : 'border-white'
              } ${
                isBusy ? 'bg-rose-500 animate-ping' : isFree ? 'bg-emerald-500' : 'bg-gray-400'
              }`}
            />
          </div>

          <div className="min-w-0">
            <h3
              className={`text-sm font-bold truncate mb-0 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
              title={agent.name}
            >
              {agent.name}
            </h3>
            <span
              className={`inline-block font-mono text-xs font-bold rounded px-1.5 mt-0.5 border ${
                isDarkMode
                  ? 'bg-teal-950/80 text-teal-300 border-teal-800/50'
                  : 'bg-blue-50 text-blue-700 border-blue-200'
              }`}
              dir="ltr"
            >
              {agent.extension}
            </span>
          </div>
        </div>

        {/* Status Chip */}
        <div className="flex items-center justify-between pt-1">
          {isBusy ? (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border animate-pulse ${
                isDarkMode
                  ? 'bg-rose-950/60 text-rose-300 border-rose-800/60'
                  : 'bg-rose-50 text-rose-700 border-rose-300'
              }`}
            >
              <AudioOutlined />
              <span>مشغول (مکالمه / زنگ)</span>
            </span>
          ) : isFree ? (
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${
                isDarkMode
                  ? 'bg-emerald-950/60 text-emerald-300 border-emerald-800/60'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-300'
              }`}
            >
              <CheckCircleOutlined className={isDarkMode ? 'text-emerald-400' : 'text-emerald-500'} />
              <span>آزاد (آماده پاسخ)</span>
            </span>
          ) : (
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-xs font-medium border ${
                isDarkMode
                  ? 'bg-[#0b1220] text-gray-400 border-gray-800'
                  : 'bg-gray-100 text-gray-500 border-gray-200'
              }`}
            >
              <StopOutlined className="text-gray-400" />
              <span>آفلاین</span>
            </span>
          )}

          {isBusy && elapsedSec > 0 && (
            <span
              className={`font-mono font-bold text-xs ${isDarkMode ? 'text-rose-400' : 'text-rose-700'}`}
              dir="ltr"
            >
              ⏱ {formatSeconds(elapsedSec)}
            </span>
          )}
        </div>

        {/* Live Call Details (if Busy) */}
        {isBusy && liveCall && (
          <div
            className={`p-2.5 rounded-lg border text-xs space-y-1 ${
              isDarkMode
                ? 'bg-rose-950/30 border-rose-800/50 text-rose-200'
                : 'bg-rose-50 border-rose-200 text-rose-700'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className={isDarkMode ? 'text-rose-300 font-medium' : 'text-rose-700 font-medium'}>
                {liveCall.direction === 'outgoing' ? 'تماس خروجی با:' : 'تماس ورودی از:'}
              </span>
              <span
                className={`font-mono font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}
                dir="ltr"
              >
                {liveCall.phone || 'تماس در جریان'}
              </span>
            </div>
            {liveCall.contact_name && (
              <div
                className={`font-semibold truncate flex items-center gap-1 ${
                  isDarkMode ? 'text-teal-300' : 'text-indigo-900'
                }`}
              >
                <UserOutlined className="text-[10px]" />
                <span>{liveCall.contact_name}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveCallCenter;
